import { Contact } from "@/components/jobs/SubmitJob";
import { CATEGORY_MCP_SERVER, JOB_TYPE, Patterns, PLATFORM, SUBMIT_JOB_CONTENT, TARGET_DOMAIN_PLCS } from "@/constants/jobConstants";
import { PUBLISHING, UP1_DOMAIN } from "@/constants/commonConstants";
import { RELEASE_JOB_TYPE } from "@/constants/releaseConstants";
import { formatChannelLabel } from "@/utilities/jobs/profileUtils";
import { isProfileImagePublishing, parseProfileJobTypes } from "@/utilities/jobs/profileConstraints";
import _ from "lodash";

type LanguageEntry = { value: string; children: string; shortName: string };

/** Derives the same short display label from children as former displayLabel (e.g. "en-US - English - United States" -> "English - United States"). */
function getLanguageLabelFromChildren(children: string): string {
  const sep = " - ";
  const idx = children.indexOf(sep);
  if (idx === -1) return children;
  return children.slice(idx + sep.length).trim();
}

/**
 * Resolves one or more language codes (e.g. "en-US" or "en-US;ru;ko;fr-CA") to a single
 * display string using SUBMIT_JOB_CONTENT.languages (same format as before, derived from children), joined by ", ".
 */
export function getLanguageDisplayString(languageCodes: string | null | undefined): string {
  if (languageCodes === null || languageCodes === undefined || String(languageCodes).trim() === "") {
    return "";
  }
  const codes = String(languageCodes)
    .split(";")
    .map((c) => c.trim())
    .filter(Boolean);
  const languages = SUBMIT_JOB_CONTENT.languages as LanguageEntry[];
  const labels = codes.map((code) => {
    const exact = languages.find((el) => el.value === code || el.value.toLowerCase() === code.toLowerCase());
    if (exact) return getLanguageLabelFromChildren(exact.children);
    const byPrefix = languages.find(
      (el) => code.length >= 2 && (el.value.toLowerCase().startsWith(code.toLowerCase() + "-") || el.value.toLowerCase() === code.toLowerCase())
    );
    if (byPrefix) return getLanguageLabelFromChildren(byPrefix.children);
    return code;
  });
  return labels.join(", ");
}

export const hasContactsError = (contacts: Array<Contact>, element: any) => {
  let result = false;
  if (contacts?.length) {
    try {
      contacts.forEach(({ index }) => {
        if ((element.includes("Name") && element === `pName_${index}`) || (element.includes("Email") && element === `pEmail_${index}`)) {
          result = true;
          throw Error();
        }
      });
    } catch (err) {
      //
    }
  }
  return result;
};

export const getImageTypeList = (jobType: string) => {
  let imageTypeList = [
    {
      children: "Select...",
      value: ""
    }
  ];
  switch (jobType) {
    case "di":
      return imageTypeList.concat(SUBMIT_JOB_CONTENT.imageTypes.filter((el) => ["Bundle", "Package"].includes(el.children)));
    case "db":
      return imageTypeList.concat(SUBMIT_JOB_CONTENT.imageTypes);
    default:
      return imageTypeList;
  }
};

export const getReviewPageContent: Record<string, any> = (submitJob: Record<string, any>) => {
  let pageContent: Record<string, any> = {};
  const jobDetails = structuredClone(submitJob);
  const { jobType } = getJobImageTypeFromData(jobDetails);

  Object.entries(jobDetails).forEach(([key, value]) => {
    switch (key) {
      case "publishing":
        pageContent[key] = { title: "What are you publishing", value: value || "" };
        break;
      case "teamName":
        pageContent[key] = { title: "Team Name", value: value || "" };
        break;
      case "yml":
        pageContent[key] = { title: ".Yaml template", value: _.capitalize(value || "") };
        break;
      case "ymlFile":
        pageContent[key] = { title: ".Yaml file", value: value || "" };
        break;
      case "contacts":
        if (value?.length == 0) {
          return (pageContent[key] = { title: "", value: [] });
        } else {
          pageContent[key] = { title: "", value };
        }
        break;
      case "dryRun":
        pageContent[key] = { title: "Is this a dry run?", value: !!value ? _.capitalize(value) : "" };
        break;
      case "incrementalUpdate":
        pageContent[key] = { title: "Is this an incremental update?", value: value ? _.capitalize(value) : "No" };
        break;
      case "jobType":
        pageContent[key] = { title: "Job Type", value: _.upperCase(value || "") };
        break;
      case "release":
        pageContent[key] = { title: "Release", value: value || "" };
        break;
      case "platform":
        pageContent[key] = { title: "Platform", value: _.capitalize(value || "") };
        break;
      case "imageType":
        pageContent[key] = { title: "Image Type", value: _.capitalize(value || "") };
        break;
      case "sourceRoot":
        pageContent[key] = { title: "Source Root", value: !!value ? value : "" };
        break;
      case "sourcePath":
        pageContent[key] = { title: "Source Path", value: !!value ? value : "" };
        break;
      case "componentPath":
        pageContent[key] = { title: "Component Path", value: !!value ? value : "" };
        break;
      case "rootManifestPath":
        pageContent[key] = { title: "Root Manifest Path", value: !!value ? value : "" };
        break;
      case "imageFileName":
        pageContent[key] = { title: "Image File Name", value: !!value ? value : "" };
        break;
      case "outputPath":
        pageContent[key] = { title: "Output path", value: !!value ? value : "" };
        break;
      case "plc":
        pageContent[key] = { title: "Item", value: !!value ? value : "" };
        break;
      case "parentJobProfile":
        pageContent[key] = {
          title: "Job Profile",
          value: value || SUBMIT_JOB_CONTENT.itemInfo.systemDefaultProfileReview
        };
        break;
      case "channel":
        pageContent[key] = { title: "Channel", value: formatChannelLabel(value as string) };
        break;
      case "tag":
        pageContent[key] = { title: "Channel Tag", value: value || "" };
        break;
      case "subChannel":
        pageContent[key] = { title: "Sub-Channel", value: value || "" };
        break;
      case "displayName":
        pageContent[key] = { title: "Display Name", value: !!value ? value : "" };
        break;
      case "name":
        pageContent[key] = { title: "Name", value: !!value ? value : "" };
        break;
      case "country":
        pageContent[key] = { title: "Country Of Origin", value: _.upperCase(value || "") };
        break;
      case "language":
        const fullLanguageName = SUBMIT_JOB_CONTENT.languages.find((el) => el.value === value)?.children || "";
        pageContent[key] = { title: "Language", value: fullLanguageName };
        break;
      case "partNumber":
        pageContent[key] = { title: "Part Number", value: !!value ? value : "" };
        break;
      case "sapDescription":
        pageContent[key] = { title: "SAP Description", value: !!value ? value : "" };
        break;
      case "targetDomain":
        {
          let displayValue = "";
          if (jobType === "df") {
            const isTargetDomainDisabledByPlc = !TARGET_DOMAIN_PLCS.includes(String(jobDetails.plc || "").toUpperCase());
            if (value) {
              displayValue = value as string;
            } else if (isTargetDomainDisabledByPlc) {
              displayValue = UP1_DOMAIN;
            }
          }
          pageContent[key] = { title: "Target Domain", value: displayValue };
        }
        break;
      default:
      //
    }
  });

  return pageContent;
};

/** Stable string for effect deps when job type may be `"ds"` or `["di","db"]` (new array each render). */
export const getJobTypeDependencyKey = (jobType: string | string[] | undefined): string => {
  if (typeof jobType === "string") return jobType;
  if (Array.isArray(jobType)) return jobType.join("|");
  return "";
};

export interface ProfileImageOptions {
  /** Install checkbox (di) is enabled. */
  diEnabled: boolean;
  /** Download checkbox (db) is enabled. */
  dbEnabled: boolean;
  /** Component Path is enabled, and therefore required. */
  componentPathEnabled: boolean;
}

/**
 * Install / Download / Component Path availability from the selected profile channel's Publish.JobType
 * (submitJob.profileJobTypes). null when the job isn't profile-constrained (no profile, republish) or has
 * no image (file types) — callers then use the legacy rules.
 */
export const getProfileImageOptions = (submitJob: Record<any, any>): ProfileImageOptions | null => {
  const jobTypes = parseProfileJobTypes(submitJob?.profileJobTypes);
  if (!jobTypes || !isProfileImagePublishing(submitJob?.publishing)) return null;
  return {
    diEnabled: jobTypes.di,
    dbEnabled: jobTypes.db,
    // ds: the Component Path is the image. db PreBuilt: it is the prebuilt SFX, only while Download is ticked.
    componentPathEnabled: jobTypes.ds || (jobTypes.db && jobTypes.dbPreBuilt && !!submitJob.productDBSelect)
  };
};

/**
 * Candidate build types for a profile-constrained job; getSelectedJobTypes() narrows di/db to the ticked boxes.
 * Component Path rides on the Download job, so "ds" is only a job of its own when no Download job is submitted.
 */
const getProfileJobTypeCandidates = (submitJob: Record<any, any>, options: ProfileImageOptions): string[] => {
  const jobTypes = parseProfileJobTypes(submitJob.profileJobTypes);
  const submitsDownload = options.dbEnabled && !!submitJob.productDBSelect;
  return [
    ...(options.diEnabled ? ["di"] : []),
    ...(options.dbEnabled ? ["db"] : []),
    ...(jobTypes?.ds && !submitsDownload ? ["ds"] : [])
  ];
};

export const getJobImageTypeFromData = (submitJob: Record<any, any>) => {
  const { publishing, platform, sourcePath, odisOnboarded, channelJobTypeOverride } = submitJob;
  let jobType: any = "";
  let imageType = "";

  const profileImageOptions = getProfileImageOptions(submitJob);
  if (profileImageOptions) {
    return {
      jobType: getProfileJobTypeCandidates(submitJob, profileImageOptions),
      imageType: isUpdateLikePublishing(publishing) ? "update" : publishing === PUBLISHING.COMPONENT ? "" : "bundle"
    };
  }

  const publishingLowerCase = publishing?.toLowerCase();

  switch (publishingLowerCase) {
    case "language pack":
    case "library":
    case "update":
    case "plug-in":
    case "engine":
    case "extension":
      imageType = "update";
      // Defaults to "ds", but is overridden by the selected profile/channel's build
      // type (di/db/ds) when it unambiguously specifies one (see channelJobTypeOverride).
      jobType = channelJobTypeOverride || "ds";
      break;
    case "file (release notes, icons etc...)":
    case "file (release notes, etc...)":
    case "component update":
    case "badge/icon":
      imageType = "file";
      jobType = "df";
      break;
    case "product":
      imageType = "bundle";
      // Product only: use effective platform (ODIS onboarded false => mac-like => ds)
      jobType =
        getEffectivePlatformForProduct(publishing, platform || PLATFORM.WINDOWS, odisOnboarded) === PLATFORM.WINDOWS
          ? ["di", "db"]
          : "ds";
      break;
    case "product (major upgrade)":
      imageType = "bundle";
      jobType = platform == PLATFORM.WINDOWS ? ["di", "db"] : "ds";
      break;
    case "component":
      jobType = ["di", "db"];
      break;
    case "config file":
      imageType = "file";
      jobType = "dg";
      break;
  }

  return { jobType, imageType };
};
const platformMappings: Record<string, string> = {
  windows: "win",
  mac: "mac",
  linux: "linux"
};

export const getSuggestedImageFileName = (submitJob: Record<string, string | boolean | Array<Contact>>) => {
  const { publishing, plc, release, language, platform, sourcePath, productDISelect, productDBSelect } = submitJob;
  const { jobType } = getJobImageTypeFromData(submitJob);
  if (jobType && jobType == "df") {
    const filename =
      typeof sourcePath === "string"
        ? sourcePath
          .split(/[\\\/]/)
          .filter(Boolean)
          .pop() || ""
        : "";
    return filename;
  }
  let suggestedName = [];
  if (plc && release && language && platform) {
    suggestedName.push(plc);
    suggestedName.push(release);
    const languageName = SUBMIT_JOB_CONTENT.languages.find((lang) => lang.value === language)?.shortName;
    if (languageName) {
      suggestedName.push(languageName);
      suggestedName.push(platformMappings[platform as string]);
      if (publishing) {
        if (jobType) {
          if (Array.isArray(jobType) && jobType.includes("db") && productDBSelect) {
            // suggestedName.push("db");
          } else {
            // suggestedName.push("di");
          }
        }
      }
    }
    return suggestedName.join("_");
  }
  return "";
};

export const generateSourcePath = (root: string, path: string) => {
  if (Patterns.s3SourcePathPattern1.test(path) || path === "") {
    return path;
  }
  if (!root.endsWith("/") && !path.startsWith("/")) {
    return root.concat("/").concat(path);
  }
  return root.concat(path);
};

export const getSuggestedOutputPath = (apiEnv: string, submitJob: Record<string, string | boolean | Array<Contact>>) => {
  const { plc, release, rootUPI } = submitJob as any;
  if (plc && release) {
    const upi = rootUPI && String(rootUPI).trim() !== "" ? rootUPI : ""; //"%UPI2%";
    return `${apiEnv}/${release}/${plc}/${upi}`;
  }
  return "";
};

export const getPartNumberByType = (publishing: string, platform: string, type: string, partNumber: string) => {
  const publishingLowerCase = publishing?.toLowerCase();
  if (!(platform == PLATFORM.WINDOWS && (publishingLowerCase == "product" || publishingLowerCase == "product (major upgrade)"))) {
    return partNumber;
  }
  if (!partNumber || partNumber == "") {
    return partNumber;
  }
  const replaceCode = type == "db" ? "DB" : "IN";
  const partToReplace = partNumber?.split("-");
  const typeCode = partToReplace[1]?.slice(4, 6);
  const typeCodeUpper = typeCode.toUpperCase();
  if (typeCodeUpper == "DB" || typeCodeUpper == "IN" || typeCodeUpper == "DI") {
    const replaceSection = partToReplace[1].replace(typeCode, replaceCode);
    return partNumber.replace(partToReplace[1], replaceSection);
  }
  return partNumber;
};

export const getDescriptionByType = (publishing: string, platform: string, type: string, sapDescription: string) => {
  const publishingLowerCase = publishing?.toLowerCase();
  if (!(platform == PLATFORM.WINDOWS && (publishingLowerCase == "product" || publishingLowerCase == "product (major upgrade)"))) {
    return sapDescription;
  }
  if (!sapDescription || sapDescription == "") {
    return sapDescription;
  }
  const replaceCode = type == "db" ? "DB" : "IN";
  const typeCode = sapDescription?.slice(0, 2);
  const typeCodeUpper = typeCode.toUpperCase();
  if (typeCodeUpper == "DB" || typeCodeUpper == "IN" || typeCodeUpper == "DI") {
    return replaceCode + sapDescription.slice(2);
  }
  return sapDescription;
};

export const getImageFileNameByType = (type: string, name: string) => {
  const checkStr = name.slice(-3).toLowerCase();
  if (checkStr == "_db" || checkStr == "_di" || checkStr == "_ds" || checkStr == "_df" || checkStr == "_dg" || checkStr == "_dt") {
    return name.replace(checkStr, `_${type}`);
  } else {
    return name.concat(`_${type}`);
  }

  return name;
};

export const isPartNumberDescVisible = (publishing: string) =>
  publishing?.toLowerCase() === "product" || publishing?.toLowerCase() === "product (major upgrade)";

/**
 * For Product only (not Product (Major Upgrade)): when odisOnboarded is false, treat as non-Windows (e.g. mac) for visibility/job-type.
 * Otherwise return platform as-is.
 */
export const getEffectivePlatformForProduct = (
  publishing: string,
  platform: string,
  odisOnboarded?: boolean
): string => {
  if (publishing === "Product" && odisOnboarded === false) return "mac";
  return platform;
};

export const isDiDbVisible = (publishing: string, platform: string, odisOnboarded?: boolean) => {
  const effectivePlatform =
    publishing === "Product" ? getEffectivePlatformForProduct(publishing, platform, odisOnboarded) : platform;
  return (
    ((publishing === "Product" && effectivePlatform === PLATFORM.WINDOWS) ||
      (publishing === "Product (Major Upgrade)" && platform === PLATFORM.WINDOWS) ||
      publishing === "Component")
  );
};

export const isImageFileNameVisible = (
  publishing: string,
  platform: string,
  productDBSelect: boolean,
  odisOnboarded?: boolean
) => publishing !== "Component" && isDiDbVisible(publishing, platform, odisOnboarded) && productDBSelect;

export const isSourceRootVisible = (publishing: string) => publishing === PUBLISHING.COMPONENT;

export const isOutputPathVisible = (publishing: string, type: string) => publishing.toLowerCase() !== "badge/icon" && type === "df";

export const isIncrementalUpdateVisible = (publishing: string, yml: string, ymlFile: string) => false;
// publishing === RELEASE_JOB_TYPE.UPDATE && (yml === "NO" || (yml === "YES" && ymlFile));

export const cleanUrl = (value: string): string =>
  String(value || "")
    .trim()
    .replace(/^[^a-zA-Z0-9]+/, "")
    .replace(/\/+$/, "");

export const getS3RootFromUrl = (sourcePath: string, s3bucket?: string | null, s3key?: string | null): string => {
  if (s3bucket && s3key) return `s3://${s3bucket}/${s3key}`;

  const [, bucket = "", key = ""] =
    String(sourcePath || "")
      .replace(/\\/g, "/")
      .match(/^s3:\/\/([^/]+)\/?(.*)$/i) || [];
  if (!bucket) return "";

  const parent = key.split("/").filter(Boolean).slice(0, -1).join("/");
  return parent ? `s3://${bucket}/${parent}` : `s3://${bucket}`;
};

const UPDATE_LIKE_RELEASE_TYPES: string[] = [
  RELEASE_JOB_TYPE.UPDATE,
  RELEASE_JOB_TYPE.EXTENSION,
  RELEASE_JOB_TYPE.LIBRARY,
  RELEASE_JOB_TYPE.LANGUAGE_PACK,
  RELEASE_JOB_TYPE.PLUGIN,
  RELEASE_JOB_TYPE.ENGINE
];

/** Update-like publishing types (Extension, Library, Update, Plug-in, Engine, Language Pack). */
export const isUpdateLikePublishing = (publishing: string | undefined): boolean =>
  UPDATE_LIKE_RELEASE_TYPES.includes(publishing ?? "");

/**
 * Build type used for submit/validation on update-like jobs: profile channel override, else "ds".
 * For Product/Component arrays, narrows to a single selected di/db when only one is checked.
 */
export const getEffectiveChannelJobType = (submitJob: Record<string, any>): string => {
  const { jobType } = getJobImageTypeFromData(submitJob);
  if (typeof jobType === "string") {
    return jobType;
  }
  if (Array.isArray(jobType)) {
    const selected = getSelectedJobTypes(jobType, submitJob.productDISelect, submitJob.productDBSelect);
    if (selected.length === 1) {
      return selected[0];
    }
  }
  return "ds";
};

/** UCT install descriptor on Source Path for profile di/db Windows update-like jobs. */
export const isSetupXmlUctSourcePathOverride = (
  publishing: string | undefined,
  platform: string | undefined,
  effectiveJobType: string,
  sourceBasename: string
): boolean => {
  if (!isUpdateLikePublishing(publishing)) {
    return false;
  }
  if (String(platform || "").toLowerCase() !== PLATFORM.WINDOWS.toLowerCase()) {
    return false;
  }
  if (effectiveJobType !== "di" && effectiveJobType !== "db") {
    return false;
  }
  return /^setup\.xml$/i.test(sourceBasename.trim());
};

const includesJobType = (jobImageType: string | string[], type: string): boolean =>
  Array.isArray(jobImageType) ? jobImageType.includes(type) : jobImageType === type;

/**
 * A Windows db update-like image is the one case where Component Path is accepted but not
 * required: APPS builds the SFX itself through UCT when no ComponentPath is sent, and falls
 * back to the dj transform when one is. Every other case that shows the field needs it.
 */
export const isComponentPathOptional = (
  publishing: string,
  platform: string,
  jobImageType: string | string[]
): boolean =>
  includesJobType(jobImageType, "db") &&
  String(platform || "").toLowerCase() === PLATFORM.WINDOWS &&
  UPDATE_LIKE_RELEASE_TYPES.includes(publishing);

/**
 * Republish loads with editJobId set, so profile/channel constraints do not run and Update-like
 * jobs would otherwise default to build type "ds". Mirror the row being republished (di/db/ds).
 */
export const getRepublishImageInfoPatch = (
  jobOutputType: string | undefined
): {
  channelJobTypeOverride: string;
  productDISelect: boolean;
  productDBSelect: boolean;
} => {
  const type = String(jobOutputType || "").toLowerCase();
  if (type === "di") {
    return { channelJobTypeOverride: "di", productDISelect: true, productDBSelect: false };
  }
  if (type === "db") {
    return { channelJobTypeOverride: "db", productDISelect: false, productDBSelect: true };
  }
  if (type === "ds") {
    return { channelJobTypeOverride: "ds", productDISelect: false, productDBSelect: false };
  }
  return { channelJobTypeOverride: "", productDISelect: false, productDBSelect: true };
};

export const isComponentPathRequired = (
  publishing: string,
  platform: string,
  odisOnboarded: boolean | undefined,
  productDBSelect: boolean,
  jobImageType: string | string[]
): boolean => {
  if (isComponentPathOptional(publishing, platform, jobImageType)) {
    return false;
  }
  const isWindows = String(platform || "").toLowerCase() === PLATFORM.WINDOWS;
  if (publishing === PUBLISHING.COMPONENT && isWindows && productDBSelect) {
    return true;
  }
  return isComponentPathVisibleForConsolidateJob(publishing, odisOnboarded !== false, platform, jobImageType);
};

/** Component Path requirement for the current form, honouring the profile channel when it constrains the job. */
export const isComponentPathRequiredForJob = (submitJob: Record<any, any>): boolean => {
  const options = getProfileImageOptions(submitJob);
  if (options) return options.componentPathEnabled;
  const { publishing, platform, odisOnboarded, productDBSelect } = submitJob;
  return isComponentPathRequired(publishing, platform, odisOnboarded, !!productDBSelect, getJobImageTypeFromData(submitJob).jobType);
};

/** Component Path accepted but not required; never the case for profile-constrained jobs (required whenever enabled). */
export const isComponentPathOptionalForJob = (submitJob: Record<any, any>): boolean => {
  if (getProfileImageOptions(submitJob)) return false;
  const { publishing, platform } = submitJob;
  return isComponentPathOptional(publishing, platform, getJobImageTypeFromData(submitJob).jobType);
};

/** Image File Name is shown for a Product / Major Upgrade Download image. */
export const isImageFileNameVisibleForJob = (submitJob: Record<any, any>): boolean => {
  const { publishing, platform, productDBSelect, odisOnboarded } = submitJob;
  const options = getProfileImageOptions(submitJob);
  if (options) {
    return isPartNumberDescVisible(publishing) && options.dbEnabled && !!productDBSelect;
  }
  return isImageFileNameVisible(publishing, platform, !!productDBSelect, odisOnboarded);
};

/**
 * getJobImageTypeFromData() returns a fixed [di, db] pair for release types where both are always
 * candidates (e.g. Component, regardless of platform) -- it doesn't know which one(s) the user
 * actually selected via the Install/Download checkboxes. Narrow it to what's actually going to be
 * submitted, so a deselected output type can't trigger visibility/validation/payload changes meant
 * for the other one.
 */
export const getSelectedJobTypes = (
  jobType: string | string[] | undefined,
  productDISelect: boolean,
  productDBSelect: boolean
): string[] => {
  if (!jobType) return [];
  if (!Array.isArray(jobType)) return [jobType];
  return jobType.filter((type) => {
    if (type === "di") return !!productDISelect;
    if (type === "db") return !!productDBSelect;
    return true;
  });
};

const isZipSourcePath = (sourcePath: string): boolean => /\.zip$/i.test((sourcePath || "").trim());

const isS3SourcePath = (sourcePath: string): boolean => {
  const trimmed = (sourcePath || "").trim();
  return Patterns.s3SourcePathPattern1.test(trimmed) || Patterns.s3SourcePathPattern2.test(trimmed);
};

export const isRootManifestPathVisible = (sourcePath: string): boolean => isZipSourcePath(sourcePath);

/**
 * Allows a .zip source path to pass extension validation when the root-manifest workflow
 * applies but platform-specific filename rules would otherwise reject it (e.g. Windows Major Upgrade).
 */
export const isZipSourcePathExtensionOverride = (
  jobType: string | string[] | undefined,
  platform: string,
  sourcePath: string,
  odisOnboarded?: boolean
): boolean => {
  const jobTypes = Array.isArray(jobType) ? jobType : [jobType];
  const isDsOrDbJob = jobTypes.includes("ds") || jobTypes.includes("db");
  const isZip = isZipSourcePath(sourcePath);
  if (!isDsOrDbJob || !isZip) {
    return false;
  }

  // platform casing is inconsistent across callers (e.g. "Mac" vs "mac"), so compare case-insensitively
  const normalizedPlatform = (platform || "").toLowerCase();
  const isMacOrLinux =
    normalizedPlatform === PLATFORM.MAC.toLowerCase() || normalizedPlatform === PLATFORM.LINUX.toLowerCase();
  const isWindowsNonOdis =
    normalizedPlatform === PLATFORM.WINDOWS.toLowerCase() && odisOnboarded === false && isS3SourcePath(sourcePath);

  return isMacOrLinux || isWindowsNonOdis;
};

/**
 * The apps service expects a single SourcePath; when a root manifest reference is required
 * (see isRootManifestPathVisible), it must be spliced in as "{rootManifestPath};{sourcePath}".
 */
export const getFinalSourcePath = (sourcePath: string, rootManifestPath: string): string => {
  const trimmedSourcePath = (sourcePath || "").trim();
  if (rootManifestPath && isRootManifestPathVisible(trimmedSourcePath)) {
    return `${rootManifestPath.trim()};${trimmedSourcePath}`;
  }
  return trimmedSourcePath;
};

/**
 * Inverse of getFinalSourcePath: splits "{rootManifestPath};{sourcePath}" back into form fields.
 * Uses first ";" only (source path may contain more); .xml/.zip suffixes confirm wire format.
 */
export const splitStoredSourcePath = (storedSourcePath: string): { sourcePath: string; rootManifestPath: string } => {
  const trimmed = (storedSourcePath || "").trim();
  const idx = trimmed.indexOf(";");
  if (idx !== -1) {
    const left = trimmed.slice(0, idx);
    const right = trimmed.slice(idx + 1);
    if (left.toLowerCase().endsWith(".xml") && right.toLowerCase().endsWith(".zip")) {
      return { sourcePath: right, rootManifestPath: left };
    }
  }
  return { sourcePath: trimmed, rootManifestPath: "" };
};

export const isComponentPathVisibleForConsolidateJob = (
  publishing: string,
  odisOnboarded: boolean,
  platform: string,
  jobImageType: string | string[]
) => {
  let isDsJob = false;
  if (Array.isArray(jobImageType) && jobImageType.includes("ds")) {
    isDsJob = true;
  } else if (typeof jobImageType === "string" && jobImageType === "ds") {
    isDsJob = true;
  }

  if (isDsJob) {
    if (publishing === RELEASE_JOB_TYPE.PRODUCT || publishing === RELEASE_JOB_TYPE.PRODUCT_MAJOR_UPGRADE) {
      if (platform === PLATFORM.WINDOWS && !odisOnboarded) {
        return true;
      }

      if (platform === PLATFORM.MAC || platform === PLATFORM.LINUX) {
        return true;
      }

      return false;
    }

    if (UPDATE_LIKE_RELEASE_TYPES.includes(publishing)) {
      return true;
    }
  }

  return isComponentPathOptional(publishing, platform, jobImageType);
};

/** True when a job row (jobs GET shape) is a Component; `image_type` compared case-insensitively. */
export const isComponentJob = (job: Record<string, any> | undefined): boolean =>
  String(job?.image_type ?? "").toLowerCase() === PUBLISHING.COMPONENT.toLowerCase();

/** True when a job row is a Component published as an MCP Server (`category`, or `Category`, is MCP_SERVER). */
export const isMcpServerJob = (job: Record<string, any> | undefined): boolean =>
  isComponentJob(job) && (job?.category ?? job?.Category) === CATEGORY_MCP_SERVER;
